import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Check, ExternalLink, ChevronDown, AlertCircle, Loader2, Star, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import Layout from '../components/Layout';
import { VariantSelectors, PricingBreakdown } from '../components/VariantSelectors';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { breadcrumbSchema } from '../data/seoMeta';
import { useSanityProduct, useSanityProducts } from '../hooks/useSanityProducts';
import { getProductReviews, getAverageRating } from '../data/reviews';
import { useTranslation } from '../context/TranslationContext';
import { useCart } from '../context/CartContext';
import { getSheetMusicDescription, getSheetMusicFeatures, SHEET_MUSIC_EXTRA_VIDEOS, SHEET_MUSIC_LOGO_IMAGE } from '../data/sheetMusicI18n';
import { isDigitalProduct } from '../utils/digitalRedirect';
import { SampleDownloadCard } from '../components/SampleDownloadCard';
import ReviewForm from '../components/ReviewForm';
import reviewText from '../data/reviewText';
import { hasSampleFile, samplePlatform } from '../data/sampleFiles';
import {
  isIndexedBook, bookPlatform, displayBookName,
  PLATFORM_LABEL, platformTextClass, platformBadgeClass,
} from '../utils/bookGrouping';

var API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to convert YouTube URL to embed URL
function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  
  // Handle youtube.com/shorts/ID format
  var shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
  if (shortsMatch) {
    return 'https://www.youtube.com/embed/' + shortsMatch[1];
  }
  
  // Handle youtu.be/ID format
  var shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
  if (shortMatch) {
    return 'https://www.youtube.com/embed/' + shortMatch[1];
  }
  
  // Handle youtube.com/watch?v=ID format
  var watchMatch = url.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/);
  if (watchMatch) {
    return 'https://www.youtube.com/embed/' + watchMatch[1];
  }
  
  return url;
}

// Image Gallery Component with Video on top, Images below
function ProductImageGallery({ product, language, variantImage, pauseRotate }) {
  // Get the YouTube video URL if available.
  // Priority: language-specific override → generic per-product youtubeUrl → first item in `videos[]`.
  var videoUrl = null;
  if (product.videosByLanguage && product.videosByLanguage[language]) {
    videoUrl = getYouTubeEmbedUrl(product.videosByLanguage[language]);
  } else if (product.youtubeUrl) {
    videoUrl = getYouTubeEmbedUrl(product.youtubeUrl);
  } else if (product.videos && product.videos.length && /youtu/i.test(product.videos[0])) {
    videoUrl = getYouTubeEmbedUrl(product.videos[0]);
  }

  // For ForScore / sheet-music products we never want to crop the iPad
  // screenshots — switch to object-contain. Arrangement scores live under
  // `category === 'digital'` (per the Sanity migration); they're treated the
  // same way so the score title at the top of the page isn't cropped.
  var slugStr = (product && product.slug) || '';
  var isSheetMusic = product && (
    product.category === 'sheet-music' ||
    (product.category === 'digital' && !/real-book|real-christmas|art-in-trumpet-album|forscore/.test(slugStr))
  );
  // ForScore *products* (Real Books, Christmas Book) — these are the only ones
  // that get the ForScore demo YouTube short appended to their gallery.
  // (NoteTurner used to be on this list, but Isaías clarified the video shows
  // the Real Book scrolling UI, which isn't relevant to the pedal product.)
  var isForscoreProduct = /^(real-book|real-christmas|new-real-book|forscore)/.test(slugStr);

  // Build a mixed list of carousel "slides" — each is either
  // `{ kind: 'image', src }` or `{ kind: 'video', embed }`.
  // Sheet-music products get the user-supplied YouTube short appended,
  // followed by the JiMendez logo as the final closing slide.
  var slides = React.useMemo(function() {
    var imgs = [];
    // Always seed with the canonical `image` field so the first slide is the
    // real thumbnail (some arrangements have only the JiMendez-logo placeholder
    // in `images[]`, leaving the actual artwork unreferenced).
    if (product.image) imgs.push(product.image);
    if (product.images && product.images.length > 0) {
      product.images.forEach(function(img) {
        if (!imgs.includes(img)) imgs.push(img);
      });
    }
    if (variantImage && imgs.indexOf(variantImage) === -1) {
      imgs.unshift(variantImage);
    }
    // For ForScore products, remove the logo from its original position —
    // we'll re-add it at the very end after the YouTube short.
    if (isSheetMusic) {
      imgs = imgs.filter(function(img) { return img !== SHEET_MUSIC_LOGO_IMAGE; });
    }
    var s = imgs.map(function(src) { return { kind: 'image', src: src }; });
    if (isForscoreProduct && SHEET_MUSIC_EXTRA_VIDEOS && SHEET_MUSIC_EXTRA_VIDEOS.length) {
      // Only append the ForScore demo video to ForScore-targeted products.
      SHEET_MUSIC_EXTRA_VIDEOS.forEach(function(u) {
        var embed = getYouTubeEmbedUrl(u);
        if (embed) s.push({ kind: 'video', embed: embed, src: u });
      });
    }
    if (isSheetMusic) {
      // Closing logo slide — always last
      s.push({ kind: 'image', src: SHEET_MUSIC_LOGO_IMAGE });
    }
    return s;
  }, [product, variantImage, isSheetMusic, isForscoreProduct]);
  // Backwards-compat alias used by older code below
  var imageList = slides.map(function(sl) { return sl.kind === 'image' ? sl.src : sl.embed; });
  
  var mediaState = useState(0);
  var selectedIndex = mediaState[0];
  var setSelectedIndex = mediaState[1];
  var userInteractedState = useState(false);
  var userInteracted = userInteractedState[0];
  var setUserInteracted = userInteractedState[1];

  useEffect(function() {
    setSelectedIndex(0);
    setUserInteracted(false);
  }, [language]);

  // When variant image changes, find its index in the image list and select it.
  // Also stop the auto-rotate so the chosen variant image STAYS visible
  // (without this, the carousel would rotate away after 4 seconds).
  // If the variant URL isn't in imageList (e.g. CDN suffix mismatch), we still
  // stop rotation and the gallery shows the closest available image.
  useEffect(function() {
    if (!variantImage) return;
    setUserInteracted(true);
    var idx = imageList.indexOf(variantImage);
    if (idx !== -1) {
      setSelectedIndex(idx);
    }
  }, [variantImage, imageList]);

  // Auto-rotate every 4 seconds (pauses once the user manually interacts via arrows,
  // thumbnails, or variant selection). Also pauses when the active slide is a
  // video so it doesn't cycle away while the customer is playing it.
  useEffect(function() {
    if (slides.length < 2 || userInteracted || pauseRotate) return undefined;
    if (slides[selectedIndex] && slides[selectedIndex].kind === 'video') return undefined;
    var id = setInterval(function() {
      setSelectedIndex(function(prev) { return (prev + 1) % slides.length; });
    }, 4000);
    return function() { clearInterval(id); };
  }, [slides, selectedIndex, userInteracted, pauseRotate]);

  function nextMedia() {
    setUserInteracted(true);
    setSelectedIndex(function(i) { return (i + 1) % imageList.length; });
  }

  function prevMedia() {
    setUserInteracted(true);
    setSelectedIndex(function(i) { return i === 0 ? imageList.length - 1 : i - 1; });
  }

  function jumpTo(idx) {
    setUserInteracted(true);
    setSelectedIndex(idx);
  }
  
  return (
    <div>
      {/* Video on top if available */}
      {videoUrl && (
        <div className="mb-4">
          <iframe
            src={videoUrl}
            title="Product Video"
            className="w-full aspect-video rounded-2xl bg-neutral-900"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}

      {/* Image carousel below — stacked images with opacity fade for smooth transitions */}
      <div className="relative">
        <div className={'relative w-full ' + (isSheetMusic ? 'aspect-[4/5]' : 'aspect-square') + ' rounded-2xl overflow-hidden bg-neutral-900'}>
          {slides.map(function(slide, idx) {
            var active = idx === selectedIndex;
            if (slide.kind === 'video') {
              return (
                <div
                  key={idx}
                  className={'absolute inset-0 w-full h-full transition-opacity duration-700 ' + (active ? '' : 'pointer-events-none')}
                  style={{ opacity: active ? 1 : 0 }}
                  data-testid={active ? 'product-video-slide' : undefined}
                >
                  {active ? (
                    <iframe
                      src={slide.embed}
                      title={product.name + ' video ' + (idx + 1)}
                      className="w-full h-full bg-black"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : null}
                </div>
              );
            }
            return (
              <img
                key={idx}
                src={slide.src}
                alt={product.name + ' view ' + (idx + 1)}
                data-testid={active ? 'product-image' : undefined}
                className={'absolute inset-0 w-full h-full ' + (isSheetMusic ? 'object-contain p-2' : 'object-cover') + ' transition-opacity duration-700 ' + (active ? '' : 'pointer-events-none')}
                style={{ opacity: active ? 1 : 0 }}
                referrerPolicy="no-referrer"
                draggable={false}
              />
            );
          })}
          {imageList.length > 1 && (
            <>
              <button type="button" onClick={prevMedia} aria-label="Previous image" style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }} className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-all backdrop-blur-sm cursor-pointer">
                <ChevronLeft className="w-5 h-5 pointer-events-none" />
              </button>
              <button type="button" onClick={nextMedia} aria-label="Next image" style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }} className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-all backdrop-blur-sm cursor-pointer">
                <ChevronRight className="w-5 h-5 pointer-events-none" />
              </button>
            </>
          )}
        </div>
        {/* Dot indicators */}
        {imageList.length > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            {imageList.map(function(_, idx) {
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={function() { jumpTo(idx); }}
                  style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                  aria-label={'Go to image ' + (idx + 1)}
                  data-testid={'product-dot-' + idx}
                  className={'h-1.5 rounded-full transition-all cursor-pointer ' + (idx === selectedIndex ? 'w-8 bg-[#d4af37]' : 'w-1.5 bg-white/20 hover:bg-white/40')}
                />
              );
            })}
          </div>
        )}
      </div>
      {/* Thumbnail strip (kept for fast jumping with many images) */}
      {slides.length > 1 && (
        <div className="flex gap-2 mt-4 overflow-x-auto pb-2" style={{ WebkitOverflowScrolling: 'touch' }}>
          {slides.map(function(slide, idx) {
            return (
              <button
                key={idx}
                type="button"
                onClick={function() { jumpTo(idx); }}
                style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                data-testid={'product-thumb-' + idx}
                aria-label={'Show item ' + (idx + 1)}
                className={'flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ' + (idx === selectedIndex ? 'border-[#d4af37]' : 'border-transparent opacity-60 hover:opacity-100')}
              >
                {slide.kind === 'video' ? (
                  <div className="w-full h-full bg-neutral-800 flex items-center justify-center relative">
                    <ChevronRight className="w-6 h-6 text-[#d4af37] absolute" />
                    <span className="text-white text-[10px] absolute bottom-1 right-1 bg-black/60 rounded px-1">▶</span>
                  </div>
                ) : (
                  <img src={slide.src} alt={product.name + ' ' + (idx + 1)} className={'w-full h-full ' + (isSheetMusic ? 'object-contain bg-neutral-950 p-1' : 'object-cover') + ' pointer-events-none'} referrerPolicy="no-referrer" draggable={false} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// SEO Structured Data Component
function ProductStructuredData({ product, reviews }) {
  var structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": (product.description || product.seoTitle || "").replace(/<[^>]*>/g, '').trim(),
    "image": [product.image].concat(product.images || []).filter(function(src, i, all) {
      return src && all.indexOf(src) === i;
    }),
    "sku": product.wixProductId || product.slug,
    "brand": {
      "@type": "Brand",
      "name": "JiMendez"
    },
    "offers": {
      "@type": "Offer",
      "url": "https://www.jimendez.com/product/" + product.slug,
      "priceCurrency": "USD",
      "price": product.price,
      "itemCondition": "https://schema.org/NewCondition",
      "priceValidUntil": new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      "availability": product.presale
        ? "https://schema.org/PreOrder"
        : (product.outOfStock ? "https://schema.org/OutOfStock" : "https://schema.org/InStock"),
      "seller": {
        "@type": "Organization",
        "name": "JiMendez Innovations"
      }
    }
  };

  if (product.category) {
    structuredData.category = product.category;
  }
  
  if (product.originalPrice && product.originalPrice > product.price) {
    structuredData.offers.priceValidUntil = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  }

  // Add aggregate rating if reviews exist
  var rated = (reviews || []).filter(function(r) { return Number(r.rating) > 0; });
  if (rated.length > 0) {
    var totalRating = 0;
    for (var i = 0; i < rated.length; i++) {
      totalRating += Number(rated[i].rating);
    }
    structuredData.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": (totalRating / rated.length).toFixed(1),
      "reviewCount": rated.length,
      "bestRating": "5",
      "worstRating": "1"
    };

    // Add individual reviews (up to 5 for rich snippets)
    var reviewItems = [];
    var maxReviews = Math.min(rated.length, 5);
    for (var j = 0; j < maxReviews; j++) {
      reviewItems.push({
        "@type": "Review",
        "author": { "@type": "Person", "name": rated[j].name },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": rated[j].rating,
          "bestRating": "5"
        },
        "reviewBody": rated[j].text
      });
    }
    structuredData.review = reviewItems;
  }

  useEffect(function() {
    var script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    script.id = 'product-structured-data';
    
    var existingScript = document.getElementById('product-structured-data');
    if (existingScript) {
      existingScript.remove();
    }
    document.head.appendChild(script);

    return function() {
      var scriptToRemove = document.getElementById('product-structured-data');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [product.slug, reviews]);

  return null;
}

function RichDescription(props) {
  var rich = props.rich;
  if (!rich) return null;
  var tagline = rich.tagline;
  var pullquote = rich.pullquote;
  var paragraphs = rich.paragraphs || [];
  var keyFeatures = rich.keyFeatures || [];
  return React.createElement('div', { 'data-testid': 'product-description-rich' },
    tagline ? React.createElement('p', {
      className: 'text-[#d4af37] mb-6 text-2xl md:text-3xl tracking-tight',
      style: { fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500, fontStyle: 'italic', letterSpacing: '-0.01em' }
    }, tagline) : null,
    React.createElement('div', {
      'data-testid': 'product-description',
      className: 'space-y-5 text-neutral-300 text-[17px] leading-[1.7] tracking-[-0.003em]'
    }, paragraphs.map(function(para, idx) { return React.createElement('p', { key: idx }, para); })),
    pullquote ? React.createElement('p', {
      className: 'mt-10 mb-6 text-white text-2xl md:text-3xl leading-tight tracking-tight',
      style: { fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500, letterSpacing: '-0.02em' }
    }, '\u201C' + pullquote + '\u201D') : null,
    keyFeatures.length > 0 ? React.createElement('div', { className: 'mt-10' },
      React.createElement('h3', { className: 'text-[11px] uppercase tracking-[0.22em] text-[#d4af37] mb-6 font-semibold' }, 'Key Features'),
      React.createElement('div', { className: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-7' },
        keyFeatures.map(function(f, idx) {
          return React.createElement('div', { key: idx, className: 'flex gap-3' },
            React.createElement('span', { className: 'text-2xl leading-none mt-0.5', 'aria-hidden': 'true' }, f.icon),
            React.createElement('div', null,
              React.createElement('p', { className: 'text-white font-semibold text-[15px] mb-1 tracking-tight' }, f.label),
              React.createElement('p', { className: 'text-neutral-400 text-sm leading-[1.55]' }, f.body)
            )
          );
        })
      )
    ) : null
  );
}

function ProductPage() {
  var params = useParams();
  var slug = params.slug;
  var { language, t, formatPrice } = useTranslation();
  
  // Fetch product from Sanity
  var { product, loading: productLoading, error: productError } = useSanityProduct(slug);
  
  // Fetch all products for related products section
  var { products: allProducts } = useSanityProducts({});

  var variantState = useState('');
  var selectedVariant = variantState[0];
  var setSelectedVariant = variantState[1];

  var errorState = useState('');
  var variantError = errorState[0];
  var setVariantError = errorState[1];

  var loadingState = useState(false);
  var isLoading = loadingState[0];
  var setIsLoading = loadingState[1];

  // Fetch reviews for structured data
  var reviewsState = useState([]);
  var pageReviews = reviewsState[0];
  var setPageReviews = reviewsState[1];

  var cart = useCart();
  var navigate = useNavigate();

  // Selected instrument variant for dynamic related products
  var instrumentState = useState('');
  var selectedInstrument = instrumentState[0];
  var setSelectedInstrument = instrumentState[1];

  // Generic per-variant image map fetched from backend (admin-managed).
  // Keys are lowercased variant values (e.g. "black", "trombone"), values are image URLs.
  var [variantImageMap, setVariantImageMap] = useState({});
  // Tracks the most recently picked variant value (any group), used to look up
  // the right image in variantImageMap.
  var [latestVariantPick, setLatestVariantPick] = useState('');
  // Multi-group variant selections + computed price (BelloFlex etc.)
  // Lives at the parent so we can pass the right unit price + composite variant
  // string to the cart.
  var [multiSelections, setMultiSelections] = useState({});
  var [multiPrice, setMultiPrice] = useState(null);
  var [resolvingSlug, setResolvingSlug] = useState(false);

  useEffect(function() {
    if (!slug) return;
    fetch(API_URL + '/api/products/' + encodeURIComponent(slug) + '/variant-images')
      .then(function(r) { return r.json(); })
      .then(function(d) {
        var map = {};
        (d.variant_images || []).forEach(function(vi) {
          map[(vi.variant_value || '').toLowerCase()] = vi.image_url;
        });
        setVariantImageMap(map);
      })
      .catch(function() { /* non-fatal */ });
  }, [slug]);

  function handleVariantPicked(groupIndex, value /*, groupName */) {
    setLatestVariantPick(value || '');
    // Maintain legacy NoteTurner behaviour: instrument selection (group 0) still
    // drives the related-products section.
    if (groupIndex === 0) setSelectedInstrument(value || '');
    // Also keep the legacy single-value state in sync — this is what
    // `variantsAreComplete()` checks for products that expose plain
    // `variants.options` (single-list) rather than `variants.groups`.
    // Setting it on every group-0 pick is harmless for multi-group products
    // because their validator uses `multiSelections` not `selectedVariant`.
    if (groupIndex === 0) setSelectedVariant(value || '');
    if (variantError) setVariantError('');
  }

  // Instrument-specific related product slugs (priority order)
  // Keys must match the lowercase variant option label (combined groups + legacy keys for backwards compat)
  var INSTRUMENT_RELATED = {
    // Combined NoteTurner groups (new)
    'trumpet + flugelhorn + french horn': ['muteclip', 'belloflex-mute', 'real-book-vol-1-bb', 'fingerflow', 'brass-plank', 'practice-mute', 'mutecap', 'mutemute', 'soniclift', 'brassgutter'],
    'saxophone + clarinet': ['saxolift', 'real-book-vol-1-eb', 'real-book-vol-1-bb', 'brass-plank', 'new-real-book-vol-1-eb', 'new-real-book-vol-1-bb'],
    'guitar + bass': ['real-book-vol-1-c', 'real-book-vol-2-c', 'real-book-vol-3-c', 'new-real-book-vol-1-c', 'real-christmas-book-c'],
    // Legacy single-instrument keys (still supported for other products that may use them)
    trumpet: ['muteclip', 'belloflex-mute', 'real-book-vol-1-bb', 'fingerflow', 'brass-plank', 'practice-mute', 'mutecap', 'mutemute', 'soniclift', 'brassgutter'],
    saxophone: ['saxolift', 'real-book-vol-1-eb', 'real-book-vol-1-bb', 'brass-plank', 'new-real-book-vol-1-eb', 'new-real-book-vol-1-bb'],
    clarinet: ['real-book-vol-1-bb', 'real-book-vol-1-eb', 'brass-plank', 'new-real-book-vol-1-bb'],
    trombone: ['trombuddy', 'real-book-vol-1-bass', 'real-book-vol-2-bass', 'real-book-vol-3-bass', 'brass-plank'],
    'french horn': ['brass-plank', 'real-book-vol-1-bb', 'belloflex-mute', 'muteclip'],
    'bass/guitar': ['real-book-vol-1-c', 'real-book-vol-2-c', 'real-book-vol-3-c', 'new-real-book-vol-1-c', 'real-christmas-book-c']
  };

  // Variant-specific images (NoteTurner: show instrument-specific photo when variant selected)
  // Keys must match the lowercased variant option text from products.js.
  var VARIANT_IMAGES = {
    // Current option names — `/` becomes ` / ` in the lowercase form
    'trumpet / flugel / french horn': 'https://static.wixstatic.com/media/4a2ebb_1edcb446ec0149f6838219bbd82383ae~mv2.jpg',
    'saxophone / clarinet': 'https://static.wixstatic.com/media/4a2ebb_895ee36e15ba4f70aee1a0473dc735c4~mv2.jpg',
    'trombone': 'https://static.wixstatic.com/media/4a2ebb_3c2346f82684487899e3c5f0682c1ad8~mv2.webp',
    'bass / guitar': 'https://static.wixstatic.com/media/4a2ebb_c6fcc7a2b11e42849dec68755e44d53b~mv2.webp',
    // Legacy " + " variants (kept for backwards compat with older orders / Sanity docs)
    'saxophone + clarinet': 'https://static.wixstatic.com/media/4a2ebb_895ee36e15ba4f70aee1a0473dc735c4~mv2.jpg',
    'trumpet + flugelhorn + french horn': 'https://static.wixstatic.com/media/4a2ebb_1edcb446ec0149f6838219bbd82383ae~mv2.jpg',
    'guitar + bass': 'https://static.wixstatic.com/media/4a2ebb_c6fcc7a2b11e42849dec68755e44d53b~mv2.webp',
    // Spanish translations
    'saxofón / clarinete': 'https://static.wixstatic.com/media/4a2ebb_895ee36e15ba4f70aee1a0473dc735c4~mv2.jpg',
    'trompeta / fliscorno / trompa': 'https://static.wixstatic.com/media/4a2ebb_1edcb446ec0149f6838219bbd82383ae~mv2.jpg',
    'trombón': 'https://static.wixstatic.com/media/4a2ebb_3c2346f82684487899e3c5f0682c1ad8~mv2.webp',
    'bajo / guitarra': 'https://static.wixstatic.com/media/4a2ebb_c6fcc7a2b11e42849dec68755e44d53b~mv2.webp',
    // Japanese
    'サクソフォン / クラリネット': 'https://static.wixstatic.com/media/4a2ebb_895ee36e15ba4f70aee1a0473dc735c4~mv2.jpg',
    'トランペット / フリューゲルホルン / フレンチホルン': 'https://static.wixstatic.com/media/4a2ebb_1edcb446ec0149f6838219bbd82383ae~mv2.jpg',
    'トロンボーン': 'https://static.wixstatic.com/media/4a2ebb_3c2346f82684487899e3c5f0682c1ad8~mv2.webp',
    'ベース / ギター': 'https://static.wixstatic.com/media/4a2ebb_c6fcc7a2b11e42849dec68755e44d53b~mv2.webp',
    // German
    'saxophon / klarinette': 'https://static.wixstatic.com/media/4a2ebb_895ee36e15ba4f70aee1a0473dc735c4~mv2.jpg',
    'trompete / flügelhorn / horn': 'https://static.wixstatic.com/media/4a2ebb_1edcb446ec0149f6838219bbd82383ae~mv2.jpg',
    'posaune': 'https://static.wixstatic.com/media/4a2ebb_3c2346f82684487899e3c5f0682c1ad8~mv2.webp',
    'bass / gitarre': 'https://static.wixstatic.com/media/4a2ebb_c6fcc7a2b11e42849dec68755e44d53b~mv2.webp',
    // French
    'saxophone / clarinette': 'https://static.wixstatic.com/media/4a2ebb_895ee36e15ba4f70aee1a0473dc735c4~mv2.jpg',
    'trompette / bugle / cor d\'harmonie': 'https://static.wixstatic.com/media/4a2ebb_1edcb446ec0149f6838219bbd82383ae~mv2.jpg',
    'basse / guitare': 'https://static.wixstatic.com/media/4a2ebb_c6fcc7a2b11e42849dec68755e44d53b~mv2.webp',
    // Italian
    'sassofono / clarinetto': 'https://static.wixstatic.com/media/4a2ebb_895ee36e15ba4f70aee1a0473dc735c4~mv2.jpg',
    'tromba / flicorno / corno francese': 'https://static.wixstatic.com/media/4a2ebb_1edcb446ec0149f6838219bbd82383ae~mv2.jpg',
    'basso / chitarra': 'https://static.wixstatic.com/media/4a2ebb_c6fcc7a2b11e42849dec68755e44d53b~mv2.webp'
  };

  // Override the gallery's first image when ANY variant is selected.
  // Priority: admin-managed map (latest pick) > legacy NoteTurner instrument map.
  var adminVariantImage = latestVariantPick ? variantImageMap[latestVariantPick.toLowerCase()] : null;
  var legacyVariantImage = selectedInstrument ? VARIANT_IMAGES[selectedInstrument.toLowerCase()] : null;
  var variantOverrideImage = adminVariantImage || legacyVariantImage || null;

  useEffect(function() {
    if (!slug) return;
    var staticReviews = getProductReviews(slug);
    fetch(API_URL + '/api/reviews/' + slug)
      .then(function(res) { return res.json(); })
      .then(function(data) {
        var apiReviews = data.reviews || [];
        setPageReviews(apiReviews.concat(staticReviews));
      })
      .catch(function() {
        setPageReviews(staticReviews);
      });
  }, [slug]);

  function refreshReviews() {
    var staticReviews = getProductReviews(slug);
    fetch(API_URL + '/api/reviews/' + slug)
      .then(function(res) { return res.json(); })
      .then(function(data) {
        var apiReviews = data.reviews || [];
        setPageReviews(apiReviews.concat(staticReviews));
      })
      .catch(function() {});
  }

  // Scroll to top when product changes
  useEffect(function() {
    window.scrollTo(0, 0);
  }, [slug]);

  // Legacy slug rescue: links in older emails point at pre-migration slugs
  // (e.g. "real-book-vol.1-in-bb-(indexed-forscore)"). Ask the backend for the
  // current slug and redirect instead of showing a dead end.
  useEffect(function() {
    if (productLoading || !slug) return;
    if (product && !productError) return;
    var cancelled = false;
    setResolvingSlug(true);
    fetch(API_URL + '/api/products/resolve-slug?slug=' + encodeURIComponent(slug))
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (cancelled) return;
        if (d && d.slug && d.slug !== slug) {
          // Full reload rather than client-side navigation: the product hook
          // keeps its "not found" error otherwise.
          window.location.replace('/product/' + d.slug + (window.location.hash || ''));
        } else {
          setResolvingSlug(false);
        }
      })
      .catch(function() { if (!cancelled) setResolvingSlug(false); });
    return function() { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, product, productError, productLoading]);

  // Loading state
  if (productLoading || ((!product || productError) && resolvingSlug)) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-black">
          <div className="text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-[#d4af37] animate-spin" />
            <p className="text-neutral-400">Loading product...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Not found state
  if (!product || productError) {
    return (
      <Layout>
        <SEO 
          title="Product Not Found"
          description="The product you're looking for could not be found."
          noindex={true}
        />
        <div className="min-h-screen flex items-center justify-center bg-black">
          <div className="text-center">
            <p className="text-white text-xl mb-4">Product not found</p>
            <Link to="/shop" className="text-[#d4af37] hover:underline">
              Browse all products
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  // Get related products - instrument-aware if variant selected
  var relatedProducts = [];
  var instrumentKey = selectedInstrument.toLowerCase();
  var instrumentSlugs = INSTRUMENT_RELATED[instrumentKey];

  if (instrumentSlugs && instrumentSlugs.length > 0) {
    // Show instrument-specific products in priority order
    for (var k = 0; k < instrumentSlugs.length && relatedProducts.length < 8; k++) {
      for (var m = 0; m < allProducts.length; m++) {
        if (allProducts[m].slug === instrumentSlugs[k] && allProducts[m].slug !== product.slug) {
          relatedProducts.push(allProducts[m]);
          break;
        }
      }
    }
  } else {
    // Default: same category
    for (var j = 0; j < allProducts.length; j++) {
      if (allProducts[j].category === product.category && allProducts[j].slug !== product.slug && relatedProducts.length < 4) {
        relatedProducts.push(allProducts[j]);
      }
    }
  }

  var hasDiscount = product.originalPrice && product.originalPrice > product.price;
  var savings = hasDiscount ? (product.originalPrice - product.price).toFixed(2) : 0;
  var isOutOfStock = product.outOfStock;
  var hasVariants = product.variants && product.variants.options && product.variants.options.length > 0;
  
  // Localized content: use language-specific versions when available.
  // For ForScore sheet-music products (which all share one structure) we
  // fall back to a shared per-language template + features list so they
  // render fully translated without per-product entries in products.js.
  var isSheetMusic = product.category === 'sheet-music';
  // The "Indexed Real Book for ForScore" fallback description must NOT apply
  // to plain arrangement PDFs (Isaías's original transcriptions for trumpet
  // and piano). It only fits Real Books / Christmas Book / NoteTurner.
  var isForscoreFallbackEligible = isSheetMusic && /^(real-book|real-christmas|new-real-book|noteturner|forscore)/.test(product.slug || '');
  var sheetMusicDesc = isForscoreFallbackEligible ? getSheetMusicDescription(product, language) : null;
  var sheetMusicFeatures = isForscoreFallbackEligible ? getSheetMusicFeatures(product, language) : null;
  var localizedDescription = (product.descriptionsByLanguage && product.descriptionsByLanguage[language])
    || sheetMusicDesc
    || product.description || '';
  var featuresList = (product.featuresByLanguage && product.featuresByLanguage[language])
    || sheetMusicFeatures
    || product.features || [];
  var localizedName = (product.namesByLanguage && product.namesByLanguage[language]) || product.name;
  var bookIsIndexed = isIndexedBook(product);
  var bookPlat = bookIsIndexed ? bookPlatform(product) : null;
  if (bookIsIndexed) localizedName = displayBookName({ name: localizedName, slug: product.slug, platform: product.platform });

  var isDigital = isDigitalProduct(product);
  // Digital items are now delivered by our own Dropbox-backed download system,
  // so they check out here like everything else (no ToneRegistry redirect).

  function handleMultiSelectionsChange(selections, computedTotal) {
    setMultiSelections(selections || {});
    setMultiPrice(typeof computedTotal === 'number' ? computedTotal : null);
    // Clear any "please select…" gate once the customer interacts.
    if (variantError) setVariantError('');
  }

  // Build a human-readable composite variant string from the selections,
  // e.g. "Black / Custom Color ($99.99) / Harmon Mute". Falls back to the
  // legacy single-value `selectedVariant` for products without groups.
  function buildVariantLabel() {
    var keys = Object.keys(multiSelections || {});
    if (keys.length > 0) {
      var parts = [];
      for (var i = 0; i < keys.length; i++) {
        var v = multiSelections[keys[i]];
        if (v) parts.push(v);
      }
      if (parts.length > 0) return parts.join(' / ');
    }
    return selectedVariant || selectedInstrument || null;
  }

  function effectiveUnitPrice() {
    return typeof multiPrice === 'number' && multiPrice > 0 ? multiPrice : product.price;
  }

  // Shared variant-completeness check — used by both Buy Now and Add to Cart
  // so a customer can't bypass the gate by clicking the gold "Buy Now"
  // button that skips the cart drawer.
  function variantsAreComplete() {
    var v = product && product.variants;
    if (v && Array.isArray(v.groups) && v.groups.length > 0) {
      for (var gi = 0; gi < v.groups.length; gi++) {
        var grp = v.groups[gi];
        if (grp.required === false) continue;
        if (!multiSelections[grp.name] || multiSelections[grp.name] === '') {
          return { ok: false, missing: grp.name };
        }
      }
      return { ok: true };
    }
    if (v && Array.isArray(v.options) && v.options.length > 0 && v.required !== false) {
      if (!selectedVariant || selectedVariant === '') {
        return { ok: false, missing: v.type || 'option' };
      }
    }
    return { ok: true };
  }

  function handleBuyNow() {
    if (isOutOfStock || isLoading) return;
    var chk = variantsAreComplete();
    if (!chk.ok) {
      setVariantError('Please select ' + chk.missing + ' before continuing.');
      try {
        var el = document.querySelector('[data-testid^="variant-select"], select');
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (_e) { /* SSR safety */ }
      return;
    }
    setVariantError('');
    cart.addItem(product, buildVariantLabel(), { isDigital: isDigital, variantImage: variantOverrideImage, unitPrice: effectiveUnitPrice() });
    cart.setIsOpen(false);
    navigate('/checkout');
  }

  function handleAddToCart() {
    if (isOutOfStock || isLoading) return;
    var chk = variantsAreComplete();
    if (!chk.ok) {
      setVariantError('Please select ' + chk.missing + ' before adding to cart.');
      try {
        var el2 = document.querySelector('[data-testid^="variant-select"], select');
        if (el2 && el2.scrollIntoView) el2.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (_e) { /* SSR safety */ }
      return;
    }
    setVariantError('');
    cart.addItem(product, buildVariantLabel(), { isDigital: isDigital, variantImage: variantOverrideImage, unitPrice: effectiveUnitPrice() });
  }

  function handleVariantChange(e) {
    setSelectedVariant(e.target.value);
    setVariantError('');
  }

  function renderVariantOptions() {
    if (!hasVariants) return null;
    var options = product.variants.options;
    var optionElements = [<option key="default" value="">Select {product.variants.type}</option>];
    for (var k = 0; k < options.length; k++) {
      optionElements.push(<option key={options[k]} value={options[k]}>{options[k]}</option>);
    }
    return optionElements;
  }

  function renderFeatures() {
    var items = [];
    for (var m = 0; m < featuresList.length; m++) {
      items.push(
        <li key={m} className="flex items-center gap-3 text-neutral-300">
          <span className="w-6 h-6 rounded-full bg-[#d4af37]/20 flex items-center justify-center flex-shrink-0">
            <Check className="w-4 h-4 text-[#d4af37]" />
          </span>
          {featuresList[m]}
        </li>
      );
    }
    return items;
  }

  function renderRelatedProducts() {
    var items = [];
    for (var n = 0; n < relatedProducts.length; n++) {
      items.push(<ProductCard key={relatedProducts[n].slug || relatedProducts[n]._id} product={relatedProducts[n]} index={n} />);
    }
    return items;
  }

  var effectivePrice = effectiveUnitPrice();
  var priceChanged = typeof multiPrice === 'number' && multiPrice > 0 && Math.abs(multiPrice - product.price) > 0.005;
  var priceDisplay = formatPrice(effectivePrice);
  var originalPriceDisplay = hasDiscount ? formatPrice(product.originalPrice) : '';
  var savingsDisplay = t('save') + ' ' + formatPrice(savings);
  var variantLabel = hasVariants ? product.variants.type : '';

  return (
    <Layout>
      <SEO 
        title={product.seoTitle || product.name}
        description={(product.description || 'Shop ' + product.name + ' at JiMendez.com. Professional-grade accessories for musicians.').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim().slice(0, 160).replace(/\s\S*$/, '')}
        image={product.image}
        url={'/product/' + product.slug}
        type="product"
        schema={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Shop', path: '/shop' },
          { name: product.name },
        ])}
      />
      <ProductStructuredData product={product} reviews={pageReviews} />
      
      <div className="pt-28 pb-4 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <nav className="flex items-center gap-2 text-sm text-neutral-600">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <Link to="/shop" className="hover:text-white">Shop</Link>
            <span>/</span>
            <span className="text-neutral-400">{product.name}</span>
          </nav>
        </div>
      </div>

      <section data-testid="product-detail-section" className="py-12 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="relative">
              <div className="sticky top-28">
                {product.badge && <span className="absolute top-4 left-4 z-10 px-4 py-2 text-sm font-semibold rounded-full bg-[#d4af37] text-black">{product.badge}</span>}
                <ProductImageGallery product={product} language={language} variantImage={variantOverrideImage} pauseRotate={Boolean(latestVariantPick)} />
              </div>
            </div>

            <div>
              <Link to="/shop" className="inline-flex items-center gap-2 text-neutral-500 hover:text-[#d4af37] mb-8">
                <ArrowLeft className="w-4 h-4" />
                Back to Shop
              </Link>

              {bookPlat && (
                <span
                  data-testid="product-platform-badge"
                  className={'inline-block mb-3 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ' + platformBadgeClass(bookPlat)}
                >
                  {PLATFORM_LABEL[bookPlat]} file
                </span>
              )}
              <h1 data-testid="product-title" className={'text-4xl md:text-5xl font-bold mb-2 ' + (bookPlat ? platformTextClass(bookPlat) : 'text-white')}>{localizedName}</h1>
              {product.tagline && <p className="text-[#d4af37] text-lg font-bold uppercase tracking-wide mb-4">{product.tagline}</p>}
              {product.seoTitle && <p className="text-neutral-500 mb-8">{product.seoTitle}</p>}

              <div className="flex items-baseline gap-4 mb-4">
                <span data-testid="product-detail-price" className="text-5xl font-bold text-[#d4af37]">{priceDisplay}</span>
                {hasDiscount && !priceChanged && <span className="text-2xl text-neutral-600 line-through">{originalPriceDisplay}</span>}
                {hasDiscount && !priceChanged && <span className="px-3 py-1 bg-red-500/20 text-red-400 text-sm rounded-full font-medium">{savingsDisplay}</span>}
                {product.variants && product.variants.groups && !priceChanged && <span className="text-lg text-neutral-500">starting at</span>}
              </div>

              <PricingBreakdown product={product} formatPrice={formatPrice} />

              {product.presale && (
                <div data-testid="product-preorder-banner"
                     className="mb-8 flex items-start gap-3 rounded-xl border border-[#d4af37]/40 bg-[#d4af37]/5 px-4 py-3">
                  <span className="shrink-0 mt-0.5 px-2.5 py-1 text-[10px] font-bold tracking-wider rounded-full bg-[#d4af37] text-black">
                    {t('preorder')}
                  </span>
                  <p className="text-sm text-[#d4af37]/90">{t('preorderNote')}</p>
                </div>
              )}

              <div className="mb-12" data-testid="product-description-block">
                {product.descriptionRich ? (
                  <RichDescription rich={product.descriptionRich} />
                ) : (
                  <div>
                    <h3 className="text-xs uppercase tracking-widest text-neutral-600 mb-3 font-semibold">Description</h3>
                    {/^\s*<(p|div|ul|ol|h[1-6]|strong|em|br)\b/i.test(localizedDescription) ? (
                      <div
                        data-testid="product-description"
                        className="text-neutral-300 leading-relaxed text-lg space-y-4 [&_p]:mb-4 [&_strong]:text-white [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mb-1 [&_a]:text-[#d4af37]"
                        dangerouslySetInnerHTML={{ __html: localizedDescription }}
                      />
                    ) : (
                      <div data-testid="product-description" className="text-neutral-300 leading-relaxed text-lg space-y-4">
                        {localizedDescription.split(/\n\n+/).map(function(para, idx) {
                          return <p key={idx} className="whitespace-pre-line">{para}</p>;
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {featuresList.length > 0 && !product.descriptionRich && (
                <div className="mb-10">
                  <h3 className="text-xs uppercase tracking-widest text-neutral-600 mb-4 font-semibold">Features</h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">{renderFeatures()}</ul>
                </div>
              )}

              <VariantSelectors product={product} formatPrice={formatPrice} language={language} onInstrumentChange={setSelectedInstrument} onVariantChange={handleVariantPicked} onSelectionsChange={handleMultiSelectionsChange} />

              <div className="space-y-4">
                {/* Social proof — deterministic per-slug pseudo-random based
                    on day-of-year so the number gently varies day-to-day but
                    stays stable on refresh within the same day. Hidden when
                    out of stock to avoid suggesting demand for unavailable goods. */}
                {!isOutOfStock && product.slug && (() => {
                  // Hash slug + today's day index → number in [12, 47].
                  var today = new Date();
                  var dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 86400000);
                  var hash = 0;
                  var key = String(product.slug) + ':' + dayOfYear;
                  for (var i = 0; i < key.length; i++) hash = ((hash << 5) - hash + key.charCodeAt(i)) | 0;
                  var viewers = 12 + (Math.abs(hash) % 36);
                  return (
                    <div data-testid="social-proof-viewers" className="flex items-center justify-center gap-2 text-neutral-400 text-xs">
                      <span className="inline-block w-2 h-2 rounded-full bg-emerald-400" />
                      <span>{viewers} {t('viewersToday')}</span>
                    </div>
                  );
                })()}
                {/* Scarcity nudge — only when stockLeft is explicitly low and
                    the product is otherwise purchasable. Translation goes via
                    the i18n `t(...)` helper so EVERY locale renders cleanly. */}
                {!isOutOfStock && typeof product.stockLeft === 'number' && product.stockLeft > 0 && product.stockLeft <= 5 && (
                  <div data-testid="stock-left-badge" className="flex items-center justify-center gap-2 bg-red-500/10 border border-red-500/30 text-red-300 rounded-full px-4 py-2 text-sm font-semibold">
                    <span className="inline-block w-2 h-2 rounded-full bg-red-400 animate-pulse" />
                    {t('lowStock', { count: product.stockLeft })}
                  </div>
                )}
                {hasSampleFile(product) && (
                  <SampleDownloadCard platform={samplePlatform(product)} variant="compact" />
                )}

                {isOutOfStock && <button disabled data-testid="out-of-stock-button" className="w-full flex items-center justify-center gap-3 bg-neutral-800 text-neutral-500 rounded-full px-8 py-5 font-semibold text-lg cursor-not-allowed">Out of Stock</button>}

                {isDigital && (
                  <Link
                    to="/my-downloads"
                    data-testid="product-my-downloads-button"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#d4af37]/50 text-[#d4af37] text-sm font-semibold hover:bg-[#d4af37] hover:text-black transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Already purchased? Get your download links
                  </Link>
                )}

                {!isOutOfStock && (
                  <>
                    <button onClick={handleBuyNow} disabled={isLoading} data-testid="buy-now-button" className="w-full flex items-center justify-center gap-3 bg-[#d4af37] text-black hover:bg-white rounded-full px-8 py-5 font-semibold text-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-70 disabled:cursor-wait">
                      {isLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : (isDigital ? <Download className="w-6 h-6" /> : <ShoppingCart className="w-6 h-6" />)}
                      {isLoading ? 'Creating Checkout...' : (isDigital ? 'Instant Download' : 'Buy Now')}
                    </button>
                    <button onClick={handleAddToCart} data-testid="add-to-cart-button" className="w-full flex items-center justify-center gap-3 bg-white/5 text-white border border-white/10 hover:border-[#d4af37]/50 hover:text-[#d4af37] rounded-full px-8 py-4 font-semibold transition-all duration-300">
                      <ShoppingCart className="w-5 h-5" />
                      Add to Cart
                    </button>
                    {variantError ? (
                      <p data-testid="variant-error" className="text-red-400 text-sm text-center bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" role="alert">
                        ⚠️ {variantError}
                      </p>
                    ) : null}
                    <p className="text-center text-sm text-neutral-600">Secure checkout • Apple Pay, Google Pay & Cards accepted</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {relatedProducts.length > 0 && (
        <section className="py-20 bg-neutral-950">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <h2 className="text-3xl font-bold text-white mb-10">Related Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{renderRelatedProducts()}</div>
          </div>
        </section>
      )}

      {/* Reviews Section */}
      <ReviewsSection slug={slug} productName={product ? product.name : ''} reviews={pageReviews} onReviewSubmitted={refreshReviews} />
    </Layout>
  );
}

var REVIEW_APP_NAME = { forscore: 'forScore', mobilesheets: 'MobileSheets' };

// Reviews Section Component - uses reviews passed from parent
function ReviewsSection({ slug, productName, reviews: passedReviews, onReviewSubmitted }) {
  var reviews = passedReviews || [];
  var langRef = useTranslation();
  var tx = reviewText[langRef.language] || reviewText.en;
  var sharedFamily = false;
  for (var r = 0; r < reviews.length; r++) {
    if (reviews[r].product_slug && reviews[r].product_slug !== slug) { sharedFamily = true; break; }
  }

  function calculateAvgRating() {
    if (reviews.length === 0) return 0;
    var total = 0;
    for (var i = 0; i < reviews.length; i++) {
      total += reviews[i].rating;
    }
    return (total / reviews.length).toFixed(1);
  }

  var avgRating = calculateAvgRating();

  // Deep-link support: emails link to /product/{slug}#reviews so the customer
  // lands directly on the review form.
  useEffect(function() {
    if (typeof window === 'undefined') return undefined;
    if (window.location.hash !== '#reviews') return undefined;
    var timer = setTimeout(function() {
      var el = document.getElementById('reviews');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 600);
    return function() { clearTimeout(timer); };
  }, [reviews.length]);

  return (
    <section id="reviews" className="py-20 bg-black border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {reviews.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">{tx.heading}</h2>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(function(star) {
                      return <Star key={star} className="w-5 h-5 fill-[#d4af37] text-[#d4af37]" />;
                    })}
                  </div>
                  <span className="text-white font-semibold">{avgRating}</span>
                  <span className="text-neutral-500">({reviews.length} {reviews.length === 1 ? tx.one : tx.many})</span>
                </div>
                {sharedFamily ? (
                  <p className="text-neutral-600 text-xs mt-2" data-testid="review-family-note">{tx.familyNote}</p>
                ) : null}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {reviews.map(function(review, index) {
                return (
                  <div key={index} data-testid="review-row" className="bg-neutral-900 rounded-2xl p-6 border border-white/5">
                    <div className="flex items-center gap-1 mb-3">
                      {[1,2,3,4,5].map(function(star) {
                        return <Star key={star} className={'w-4 h-4 ' + (star <= review.rating ? 'fill-[#d4af37] text-[#d4af37]' : 'text-neutral-700')} />;
                      })}
                    </div>
                    <p className="text-neutral-300 text-sm leading-relaxed mb-4">"{review.text}"</p>
                    {review.reply ? (
                      <p className="text-neutral-400 text-xs mb-4 border-l-2 border-[#d4af37]/40 pl-3">
                        <span className="text-[#d4af37]">{tx.reply}:</span> {review.reply}
                      </p>
                    ) : null}
                    {review.product_slug && review.product_slug !== slug && review.product_name ? (
                      <p className="text-neutral-600 text-xs mb-3" data-testid="review-source-product">
                        {tx.reviewFor}: {review.general_platform
                          ? tx.generalLabel.replace('{app}', REVIEW_APP_NAME[review.general_platform] || '')
                          : review.product_name}
                      </p>
                    ) : null}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium text-sm">{review.name}</p>
                        <p className="text-neutral-600 text-xs">{review.date}</p>
                      </div>
                      {review.source && <span className="text-xs text-neutral-600 bg-neutral-800 px-2 py-1 rounded">{review.source}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        
        {/* Review Submission Form */}
        <ReviewForm productSlug={slug} productName={productName} onSubmitted={onReviewSubmitted} />
      </div>
    </section>
  );
}

export default ProductPage;
